import { useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useConsent } from '../../consent/ConsentContext';
import { CATEGORIES } from '../../consent/consentConfig';

/**
 * First-party consent banner + preference center (GDPR plan P1-3).
 *
 * - Opt-in regions (EU/EEA/UK/CH, or unknown) get the compact banner on first
 *   visit / after a version bump. Everyone else gets a small notice with a
 *   "Manage cookies" link; their defaults are recorded by ConsentContext.
 * - Nothing shows on sign-in / sign-up pages (consentConfig `isAuthPath`).
 * - Equal prominence: "Reject all" and "Accept all" are the same size; no
 *   pre-ticked optional boxes; the banner cannot be dismissed without a choice.
 * - Persistent re-entry via <ManageCookiesButton/> in the footer.
 * - CCPA/CPRA "Do Not Sell or Share" is satisfied by turning off Analytics +
 *   Marketing in the preference center (noted in the UI).
 */

const COOKIE_POLICY_HREF = '/legal/cookies';
const PRIVACY_POLICY_HREF = '/legal/privacy';

function Toggle({ checked, disabled, onChange, label }) {
  return (
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-jade focus:ring-offset-2 ${
        checked ? 'bg-jade' : 'bg-gray-300 dark:bg-line'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

export default function ConsentBanner() {
  const {
    ready,
    bannerOpen,
    noticeOpen,
    noticeGpc,
    prefsOpen,
    categories,
    acceptAll,
    rejectAll,
    save,
    openPreferences,
    closePreferences,
    dismissNotice,
  } = useConsent();

  // Local working copy for the preference-center toggles (seeded on open).
  const [draft, setDraft] = useState(categories);
  useEffect(() => {
    if (prefsOpen) setDraft(categories);
  }, [prefsOpen, categories]);

  // Focus management for the preference-center modal (a11y): move focus into
  // the dialog on open, trap Tab/Shift+Tab inside it, close on Escape, and
  // restore focus to the previously-focused element on close.
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!prefsOpen) return undefined;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const previouslyFocused = document.activeElement;

    const getFocusable = () =>
      Array.from(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);

    // Move focus into the dialog (first focusable control, else the dialog).
    (getFocusable()[0] || dialog).focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePreferences();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = getFocusable();
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [prefsOpen, closePreferences]);

  // Render nothing until hydrated (SSR-safe) and only when something is open.
  if (!ready) return null;
  if (!bannerOpen && !noticeOpen && !prefsOpen) return null;

  const setCat = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  return (
    <div className='fixed inset-0 z-[2147483646] pointer-events-none'>
      {/* Preference center modal */}
      {prefsOpen && (
        <div className='pointer-events-auto fixed inset-0 flex items-center justify-center bg-black/50 p-4'>
          <div
            ref={dialogRef}
            role='dialog'
            aria-modal='true'
            aria-label='Cookie preferences'
            tabIndex={-1}
            className='w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-surface shadow-2xl border border-light-border dark:border-line focus:outline-none'
          >
            <div className='p-6'>
              <h2 className='text-lg font-semibold text-text-dark dark:text-text-light'>
                Cookie preferences
              </h2>
              <p className='mt-2 text-sm text-gray-600 dark:text-muted'>
                We use cookies and similar technologies. Strictly-necessary
                cookies are always on. Choose which optional categories to allow
                — you can change this anytime.{' '}
                <a
                  href={COOKIE_POLICY_HREF}
                  className='underline text-jade-deep dark:text-jade-soft'
                >
                  Cookie Policy
                </a>
              </p>

              <div className='mt-5 space-y-4'>
                {CATEGORIES.map((cat) => (
                  <div
                    key={cat.key}
                    className='flex items-start justify-between gap-4'
                  >
                    <div>
                      <p className='text-sm font-medium text-text-dark dark:text-text-light'>
                        {cat.title}
                      </p>
                      <p className='text-xs text-gray-500 dark:text-muted mt-0.5'>
                        {cat.description}
                      </p>
                    </div>
                    <Toggle
                      label={cat.title}
                      checked={cat.required ? true : !!draft[cat.key]}
                      disabled={cat.required}
                      onChange={(v) => setCat(cat.key, v)}
                    />
                  </div>
                ))}
              </div>

              {/* CCPA / CPRA notice (GDPR plan P1-5). */}
              <p className='mt-5 text-xs text-gray-500 dark:text-muted border-t border-light-border dark:border-line pt-4'>
                California residents: turning off Analytics and Marketing
                exercises your right to{' '}
                <span className='font-medium'>
                  Do Not Sell or Share My Personal Information
                </span>
                . See our{' '}
                <a
                  href={PRIVACY_POLICY_HREF}
                  className='underline text-jade-deep dark:text-jade-soft'
                >
                  Privacy Policy
                </a>
                .
              </p>

              <div className='mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2'>
                <button
                  type='button'
                  onClick={closePreferences}
                  className='px-4 py-2 text-sm rounded-lg text-gray-600 dark:text-muted hover:bg-gray-100 dark:hover:bg-line'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={rejectAll}
                  className='px-4 py-2 text-sm rounded-lg border border-light-border dark:border-line text-text-dark dark:text-text-light hover:bg-gray-50 dark:hover:bg-line'
                >
                  Reject all
                </button>
                <button
                  type='button'
                  onClick={() => save(draft)}
                  className='px-4 py-2 text-sm rounded-lg bg-jade text-on-jade font-medium hover:opacity-90'
                >
                  Save choices
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Opt-in regions: compact first-visit banner (hidden while the
          preference center is open). Reject and Accept stay equal weight. */}
      {bannerOpen && !prefsOpen && (
        <div
          role='region'
          aria-label='Cookie consent'
          className='pointer-events-auto fixed bottom-0 inset-x-0 sm:bottom-4 sm:inset-x-auto sm:right-4 sm:w-[22.5rem]'
        >
          <div className='rounded-none sm:rounded-xl bg-white dark:bg-surface shadow-xl border-t sm:border border-light-border dark:border-line p-4'>
            <p className='text-xs text-muted'>
              We use optional cookies for analytics and support chat, only if
              you allow them.{' '}
              <a
                href={COOKIE_POLICY_HREF}
                className='underline text-jade-deep dark:text-jade-soft'
              >
                Learn more
              </a>
            </p>
            <div className='mt-3 flex items-center gap-2'>
              <button
                type='button'
                onClick={openPreferences}
                className='mr-auto text-xs text-muted underline hover:text-text-dark dark:hover:text-text-light'
              >
                Manage
              </button>
              <button
                type='button'
                onClick={rejectAll}
                className='px-3 py-1.5 text-sm rounded-lg border border-light-border dark:border-line text-text-dark dark:text-text-light font-medium hover:bg-gray-50 dark:hover:bg-line'
              >
                Reject all
              </button>
              <button
                type='button'
                onClick={acceptAll}
                className='px-3 py-1.5 text-sm rounded-lg border border-jade bg-jade text-on-jade font-medium hover:opacity-90'
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Everywhere else: defaults are already recorded; this only says so
          and links to the preference center. */}
      {noticeOpen && !prefsOpen && (
        <div
          role='region'
          aria-label='Cookie notice'
          className='pointer-events-auto fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-xs'
        >
          <div className='flex items-start gap-3 rounded-xl bg-white dark:bg-surface shadow-xl border border-light-border dark:border-line px-3.5 py-2.5'>
            <p className='text-xs text-muted'>
              {noticeGpc
                ? 'Your browser\'s privacy signal is on, so analytics and marketing cookies are off.'
                : 'We use cookies to run and improve TimberCloud.'}{' '}
              <button
                type='button'
                onClick={openPreferences}
                className='underline text-jade-deep dark:text-jade-soft'
              >
                Manage cookies
              </button>
            </p>
            <button
              type='button'
              onClick={dismissNotice}
              aria-label='Dismiss cookie notice'
              className='-mr-1 shrink-0 rounded p-0.5 text-muted hover:text-text-dark dark:hover:text-text-light'
            >
              <FiX className='h-4 w-4' aria-hidden='true' />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
